export type ThemeTokens = Record<string, string>;

export type Theme = {
  radius: string;
  font_sans: string;
  font_mono: string;
  light: ThemeTokens;
  dark: ThemeTokens;
  preset_id?: string | null;
  updated_at?: number;
};

export const FONT_PRESETS = [
  {
    id: 'geist',
    label: 'Geist (default)',
    sans: "'Geist', ui-sans-serif, system-ui, sans-serif",
    mono: "'Geist Mono', ui-monospace, monospace",
    google: 'Geist:wght@300;400;500;600;700|Geist+Mono:wght@400;500;600',
  },
  {
    id: 'inter',
    label: 'Inter',
    sans: "'Inter', ui-sans-serif, system-ui, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
    google: 'Inter:wght@400;500;600;700|JetBrains+Mono:wght@400;500',
  },
  {
    id: 'system',
    label: 'System',
    sans: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
    mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    google: null,
  },
  {
    id: 'dm-sans',
    label: 'DM Sans',
    sans: "'DM Sans', ui-sans-serif, system-ui, sans-serif",
    mono: "'DM Mono', ui-monospace, monospace",
    google: 'DM+Sans:wght@400;500;600;700|DM+Mono:wght@400;500',
  },
  {
    id: 'ibm-plex',
    label: 'IBM Plex Sans',
    sans: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
    mono: "'IBM Plex Mono', ui-monospace, monospace",
    google: 'IBM+Plex+Sans:wght@300;400;500;600;700|IBM+Plex+Mono:wght@400;500;600',
  },
  {
    id: 'instrument',
    label: 'Instrument Sans',
    sans: "'Instrument Sans', ui-sans-serif, system-ui, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
    google: 'Instrument+Sans:wght@400;500;600;700|JetBrains+Mono:wght@400;500',
  },
] as const;

export type FontPreset = (typeof FONT_PRESETS)[number];

export const COLOR_TOKEN_GROUPS: Array<{ label: string; keys: string[] }> = [
  { label: 'Temel', keys: ['background', 'foreground'] },
  { label: 'Kart', keys: ['card', 'card-foreground'] },
  { label: 'Popover', keys: ['popover', 'popover-foreground'] },
  { label: 'Primary', keys: ['primary', 'primary-foreground'] },
  { label: 'Secondary', keys: ['secondary', 'secondary-foreground'] },
  { label: 'Muted', keys: ['muted', 'muted-foreground'] },
  { label: 'Accent', keys: ['accent', 'accent-foreground'] },
  { label: 'Destructive', keys: ['destructive', 'destructive-foreground'] },
  { label: 'Borders', keys: ['border', 'input', 'ring'] },
  {
    label: 'Sidebar',
    keys: [
      'sidebar',
      'sidebar-foreground',
      'sidebar-primary',
      'sidebar-primary-foreground',
      'sidebar-accent',
      'sidebar-accent-foreground',
      'sidebar-border',
      'sidebar-ring',
    ],
  },
];

const GOOGLE_FONT_LINK_ID = 'cms-hub-dynamic-fonts';

/** Inject the Google Fonts <link> for a given preset (remove previous). */
export function loadGoogleFont(preset: FontPreset) {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(GOOGLE_FONT_LINK_ID);
  if (existing) existing.remove();
  if (!preset.google) return;
  const link = document.createElement('link');
  link.id = GOOGLE_FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${preset.google.replaceAll('|', '&family=')}&display=swap`;
  document.head.appendChild(link);
}

/** Apply a theme by writing a <style> tag. Both light (:root) and dark (.dark)
 * live in the same stylesheet so that .dark selector wins when the class is
 * present (stylesheet specificity > :root, and both stylesheet rules beat
 * previously-applied inline styles are avoided entirely). */
export function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;

  const existing = document.getElementById('cms-hub-theme-vars');
  if (existing) existing.remove();

  const lightPairs = [
    `--radius: ${theme.radius}`,
    `--font-sans: ${theme.font_sans}`,
    `--font-mono: ${theme.font_mono}`,
    ...Object.entries(theme.light).map(([k, v]) => `--${k}: ${v}`),
  ];
  const darkPairs = Object.entries(theme.dark).map(([k, v]) => `--${k}: ${v}`);

  const style = document.createElement('style');
  style.id = 'cms-hub-theme-vars';
  style.textContent = `:root {
  ${lightPairs.join(';\n  ')};
}
.dark {
  ${darkPairs.join(';\n  ')};
}`;
  document.head.appendChild(style);
}
